/*
 * Purpose: Post-GitHub OAuth bootstrap — sync HttpOnly session cookie into client auth state
 * Last modified: 2026-07-10 | Agent
 * Completeness: 100
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { BrandMark } from '@/components/brand/BrandMark';
import { ContextualError } from '@/components/errors/ContextualError';
import { Button } from '@/components/ui/button';
import { AUTH_CONFIG } from '@/lib/auth-config';
import { authFetchErrorMessage } from '@/lib/apiBase';
import { bootstrapAuthSession } from '@/lib/authCompleteBootstrap';
import { APP_ROUTES } from '@/lib/appRoutes';
import { PRODUCT_NAME } from '@/lib/productTerms';
import { useAuthStore } from '@/stores/authStore';

export default function AuthOAuthComplete() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') || AUTH_CONFIG.postLoginPath;
  const hydrate = useAuthStore((s) => s.hydrate);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (AUTH_CONFIG.mock) {
      navigate(next, { replace: true });
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const boot = await bootstrapAuthSession(hydrate, useAuthStore.getState);
        if (boot.authed) {
          if (!cancelled) navigate(next, { replace: true });
          return;
        }
        if (!cancelled) {
          setError(
            'Sign-in completed but this browser did not keep your session. Try again or use email sign-in.',
          );
        }
      } catch (e) {
        if (!cancelled) setError(authFetchErrorMessage(e));
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [hydrate, navigate, next]);

  return (
    <div className="auth-gate-overlay min-h-screen flex flex-col bg-[var(--auth-bg)]">
      <header className="shrink-0 border-b border-[var(--auth-border)]/60 px-4 py-3 flex items-center gap-2">
        <Link to="/" className="flex items-center gap-2">
          <BrandMark size={24} ghostAccent="darker" />
          <span className="ab-brand-wordmark text-sm">{PRODUCT_NAME}</span>
        </Link>
      </header>
      <main className="flex-1 flex items-center justify-center p-4 sm:p-8">
        {busy ? (
          <p className="text-sm text-muted-foreground">Finishing GitHub sign-in…</p>
        ) : error ? (
          <div className="max-w-md w-full space-y-4">
            <ContextualError
              message={error}
              action={{
                label: 'Try GitHub again',
                href: `${APP_ROUTES.authSignIn}?next=${encodeURIComponent(next)}`,
              }}
              secondaryAction={{ label: 'Home', href: APP_ROUTES.home }}
            />
            <Button variant="outline" className="w-full" asChild>
              <Link to={`${APP_ROUTES.authSignIn}?next=${encodeURIComponent(next)}`}>
                Back to sign in
              </Link>
            </Button>
          </div>
        ) : null}
      </main>
    </div>
  );
}
